import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Calculator, TrendingUp, Clock, DollarSign, Save, Trash2,
  ChevronDown, ChevronUp, Upload, X, Code, Globe, FileDown, ExternalLink, Loader2, Sliders
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "process", label: "Prozessoptimierung", color: "#D4A373", desc: "Manuelle Abläufe straffen und automatisieren", icon: Clock },
  { id: "digital", label: "Digitalisierung", color: "#3B82F6", desc: "Papier- und Legacy-Systeme ablösen", icon: TrendingUp },
  { id: "ai", label: "KI / Automatisierung", color: "#8B5CF6", desc: "Intelligente Automatisierung einführen", icon: Calculator },
  { id: "software", label: "Softwareentwicklung", color: "#F59E0B", desc: "Massgeschneiderte Software statt manueller Workarounds", icon: Code },
  { id: "web", label: "Web & Digital Marketing", color: "#EC4899", desc: "Content, SEO, Social & Ads automatisieren", icon: Globe },
];

// 3 scenario levels per category
const SCENARIO_FACTORS = {
  process:  { conservative: 0.20, realistic: 0.30, optimistic: 0.40 },
  digital:  { conservative: 0.25, realistic: 0.40, optimistic: 0.55 },
  ai:       { conservative: 0.35, realistic: 0.55, optimistic: 0.70 },
  software: { conservative: 0.15, realistic: 0.25, optimistic: 0.35 },
  web:      { conservative: 0.20, realistic: 0.30, optimistic: 0.40 },
};

const SCENARIO_LABELS = {
  conservative: { label: "Konservativ", color: "#94A3B8" },
  realistic:    { label: "Realistisch", color: "#10B981" },
  optimistic:   { label: "Optimistisch", color: "#3B82F6" },
};

const KALK_CAT_MAP = { "01": "process", "02": "digital", "03": "ai", "04": "software", "05": "web" };

function formatCHF(n) {
  return `CHF ${Math.round(n).toLocaleString("de-CH")}`;
}

function SliderInput({ label, value, onChange, min, max, step, unit, testId }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState("");

  const startEdit = () => {
    setEditVal(String(value));
    setEditing(true);
  };

  const commitEdit = () => {
    setEditing(false);
    let num = parseFloat(editVal.replace(/[']/g, ""));
    if (isNaN(num)) return;
    num = Math.max(min, Math.min(max, Math.round(num / step) * step));
    onChange(num);
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-baseline">
        <label className="text-xs font-semibold uppercase text-[#64748B]" style={{ letterSpacing: "0.1em" }}>{label}</label>
        {editing ? (
          <input
            autoFocus
            type="text"
            value={editVal}
            onChange={e => setEditVal(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => e.key === "Enter" && commitEdit()}
            className="text-lg font-semibold text-[#0F172A] font-playfair bg-transparent border-b-2 border-[#D4A373] outline-none text-right w-28"
            data-testid={`${testId}-input`}
          />
        ) : (
          <button onClick={startEdit}
            className="text-lg font-semibold text-[#0F172A] font-playfair hover:text-[#D4A373] hover:border-b hover:border-[#D4A373] transition-colors cursor-text"
            title="Klicken zum Bearbeiten"
            data-testid={`${testId}-value`}>
            {value.toLocaleString("de-CH")} {unit}
          </button>
        )}
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-[#E2E8F0] rounded-none appearance-none cursor-pointer accent-[#D4A373]"
        data-testid={testId}
      />
      <div className="flex justify-between text-[0.6rem] text-[#94A3B8]">
        <span>{min.toLocaleString("de-CH")} {unit}</span>
        <span>{max.toLocaleString("de-CH")} {unit}</span>
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border border-[#CBD5E1] p-5 text-center">
      <Icon className="w-5 h-5 mx-auto mb-2" style={{ color }} />
      <div className="text-[0.6rem] font-bold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.12em" }}>{label}</div>
      <div className="font-playfair text-2xl font-semibold text-[#0F172A]">{value}</div>
      {sub && <div className="text-xs text-[#64748B] mt-1">{sub}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const months = typeof label === "number" ? label : 0;
  const years = Math.floor(months / 12);
  const remainMonths = months % 12;
  const timeLabel = years > 0
    ? (remainMonths > 0 ? `Jahr ${years}, Monat ${remainMonths}` : `Jahr ${years}`)
    : `Monat ${months}`;
  return (
    <div className="bg-[#0F172A] text-white text-xs px-3 py-2 border border-[#334155]">
      <div className="font-semibold mb-1">{timeLabel}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[#94A3B8]">{p.name}:</span>
          <span className="font-semibold">{formatCHF(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function OfferPickerModal({ offers, onSelect, onClose }) {
  if (!offers.length) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white p-6 max-w-md w-full mx-4 border border-[#CBD5E1]" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Aus Kalkulator laden</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-[#64748B]">Noch keine Angebote im Kalkulator erstellt.</p>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white max-w-lg w-full mx-4 border border-[#CBD5E1] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center px-5 py-4 border-b border-[#E2E8F0]">
          <h3 className="text-sm font-semibold text-[#0F172A]">Angebot wählen</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="offer-picker-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto divide-y divide-[#F1F5F9]">
          {offers.map((o, i) => {
            const cost = o.payload?.calculation?.endpreis_chf || o.payload?.calculation?.fixpreis_chf || 0;
            const cats = [...new Set((o.payload?.positions || []).map(p => p.code?.substring(0, 2)))];
            const dominant = cats.sort((a, b) =>
              (o.payload?.positions || []).filter(p => p.code?.startsWith(b)).length -
              (o.payload?.positions || []).filter(p => p.code?.startsWith(a)).length
            )[0];
            const roiCat = KALK_CAT_MAP[dominant] || "process";
            const catLabel = CATEGORIES.find(c => c.id === roiCat)?.label || "";
            return (
              <button
                key={i}
                className="w-full text-left px-5 py-3 hover:bg-[#F8FAFC] transition-colors"
                onClick={() => onSelect(o, roiCat, cost)}
                data-testid={`offer-pick-${i}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-semibold text-[#0F172A]">{o.client_name || "Ohne Kunde"}</div>
                    <div className="text-xs text-[#64748B]">
                      {o.offer_number} | {o.project_name || "—"} | {catLabel}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-[#0F172A]">{formatCHF(cost)}</div>
                    <div className="text-xs text-[#94A3B8]">{o.total_hours}h</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Calculate ROI for a given scenario
function calcScenario(category, scenario, employees, hoursWeek, hourlyCost, projectCost, annualOpsCost) {
  const timeSaving = SCENARIO_FACTORS[category][scenario];
  const hoursPerYear = employees * hoursWeek * 48;
  const hoursSaved = Math.round(hoursPerYear * timeSaving);
  const costSaved = Math.round(hoursSaved * hourlyCost);
  const netAnnualBenefit = costSaved - annualOpsCost;
  const totalCost = projectCost + annualOpsCost * 5;
  const fiveYearValue = costSaved * 5 - totalCost;
  const roi = projectCost > 0 ? Math.round(((netAnnualBenefit - projectCost) / projectCost) * 100) : 0;
  const paybackMonths = netAnnualBenefit > 0 ? Math.round((projectCost / netAnnualBenefit) * 12) : 0;
  return { timeSaving, hoursPerYear, hoursSaved, costSaved, netAnnualBenefit, fiveYearValue, roi, paybackMonths };
}

export default function ROICalculator() {
  const [category, setCategory] = useState("process");
  const [scenario, setScenario] = useState("realistic");
  const [employees, setEmployees] = useState(5);
  const [hoursWeek, setHoursWeek] = useState(10);
  const [hourlyCost, setHourlyCost] = useState(85);
  const [projectCost, setProjectCost] = useState(25000);
  const [annualOpsCost, setAnnualOpsCost] = useState(3000);
  const [clientName, setClientName] = useState("");
  const [notes, setNotes] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showOfferPicker, setShowOfferPicker] = useState(false);
  const [kalkulatorOffers, setKalkulatorOffers] = useState([]);
  const [loadedOffer, setLoadedOffer] = useState(null);
  const [generatingDossier, setGeneratingDossier] = useState(false);
  const [dossierUrl, setDossierUrl] = useState(null);

  const loadScenarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/roi`);
      setScenarios(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  // Active scenario calculations
  const active = calcScenario(category, scenario, employees, hoursWeek, hourlyCost, projectCost, annualOpsCost);

  // All 3 scenarios for comparison
  const allScenarios = useMemo(() => ({
    conservative: calcScenario(category, "conservative", employees, hoursWeek, hourlyCost, projectCost, annualOpsCost),
    realistic:    calcScenario(category, "realistic", employees, hoursWeek, hourlyCost, projectCost, annualOpsCost),
    optimistic:   calcScenario(category, "optimistic", employees, hoursWeek, hourlyCost, projectCost, annualOpsCost),
  }), [category, employees, hoursWeek, hourlyCost, projectCost, annualOpsCost]);

  // 5-Year chart data — monthly granularity for precise break-even
  const chartData = useMemo(() => {
    const data = [];
    for (let m = 0; m <= 60; m++) {
      const y = m / 12;
      data.push({
        month: m,
        year: y,
        conservative: allScenarios.conservative.costSaved * y - projectCost - annualOpsCost * y,
        realistic: allScenarios.realistic.costSaved * y - projectCost - annualOpsCost * y,
        optimistic: allScenarios.optimistic.costSaved * y - projectCost - annualOpsCost * y,
      });
    }
    return data;
  }, [allScenarios, projectCost, annualOpsCost]);

  const breakEvenMonths = useMemo(() => {
    if (active.netAnnualBenefit <= 0) return null;
    const months = Math.round((projectCost / active.netAnnualBenefit) * 12);
    return months <= 60 ? months : null;
  }, [active.netAnnualBenefit, projectCost]);

  const results = {
    hoursSaved: active.hoursSaved, costSaved: active.costSaved, roi: active.roi,
    paybackMonths: active.paybackMonths, fiveYearValue: active.fiveYearValue,
    hoursPerYear: active.hoursPerYear, netAnnualBenefit: active.netAnnualBenefit,
    scenario, annualOpsCost,
    comparison: {
      conservative: { roi: allScenarios.conservative.roi, fiveYearValue: allScenarios.conservative.fiveYearValue },
      realistic: { roi: allScenarios.realistic.roi, fiveYearValue: allScenarios.realistic.fiveYearValue },
      optimistic: { roi: allScenarios.optimistic.roi, fiveYearValue: allScenarios.optimistic.fiveYearValue },
    },
  };

  const saveScenario = async () => {
    try {
      await axios.post(`${API}/admin/roi`, {
        client_name: clientName,
        category,
        inputs: { employees, hoursWeek, hourlyCost, projectCost, annualOpsCost, scenario },
        results,
        notes,
        loaded_offer: loadedOffer,
      });
      toast.success("Szenario gespeichert");
      loadScenarios();
    } catch {
      toast.error("Fehler beim Speichern");
    }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm("Szenario löschen?")) return;
    try {
      await axios.delete(`${API}/admin/roi/${id}`);
      setScenarios(prev => prev.filter(s => s.id !== id));
      toast.success("Gelöscht");
    } catch { toast.error("Fehler"); }
  };

  const loadScenarioData = (s) => {
    const inp = s.inputs || {};
    setCategory(s.category || "process");
    setScenario(inp.scenario || "realistic");
    setEmployees(inp.employees || 5);
    setHoursWeek(inp.hoursWeek || 10);
    setHourlyCost(inp.hourlyCost || 85);
    setProjectCost(inp.projectCost || 25000);
    setAnnualOpsCost(inp.annualOpsCost || 3000);
    setClientName(s.client_name || "");
    setNotes(s.notes || "");
    setLoadedOffer(s.loaded_offer || null);
  };

  const openOfferPicker = async () => {
    try {
      const res = await axios.get(`${API}/admin/kalkulator/offers`);
      setKalkulatorOffers(res.data || []);
      setShowOfferPicker(true);
    } catch {
      toast.error("Kalkulator-Angebote konnten nicht geladen werden");
    }
  };

  const handleOfferSelect = (offer, roiCat, cost) => {
    setCategory(roiCat);
    setProjectCost(Math.min(200000, Math.max(5000, Math.round(cost / 1000) * 1000)));
    setClientName(offer.client_name || offer.payload?.client?.name || "");
    setLoadedOffer({
      offer_number: offer.offer_number,
      client_name: offer.client_name,
      fixpreis: offer.payload?.calculation?.fixpreis_chf,
      endpreis: offer.payload?.calculation?.endpreis_chf,
    });
    setShowOfferPicker(false);
    toast.success(`Angebot ${offer.offer_number} geladen`);
  };

  const generateDossier = async () => {
    setGeneratingDossier(true);
    setDossierUrl(null);
    try {
      const catLabel = CATEGORIES.find(c => c.id === category)?.label || category;
      const sf = SCENARIO_FACTORS[category];
      const res = await axios.post(`${API}/admin/roi/dossier`, {
        client_name: clientName || "Kunde",
        category,
        category_label: catLabel,
        scenario,
        inputs: {
          employees,
          hours_week: hoursWeek,
          hourly_cost: hourlyCost,
          project_cost: projectCost,
          annual_ops_cost: annualOpsCost,
        },
        scenario_factors: {
          conservative: Math.round(sf.conservative * 100),
          realistic: Math.round(sf.realistic * 100),
          optimistic: Math.round(sf.optimistic * 100),
        },
        results: {
          conservative: {
            efficiency_pct: Math.round(sf.conservative * 100),
            hours_saved: allScenarios.conservative.hoursSaved,
            cost_saved: allScenarios.conservative.costSaved,
            net_annual_benefit: allScenarios.conservative.netAnnualBenefit,
            roi: allScenarios.conservative.roi,
            payback_months: allScenarios.conservative.paybackMonths,
            five_year_value: allScenarios.conservative.fiveYearValue,
          },
          realistic: {
            efficiency_pct: Math.round(sf.realistic * 100),
            hours_saved: allScenarios.realistic.hoursSaved,
            cost_saved: allScenarios.realistic.costSaved,
            net_annual_benefit: allScenarios.realistic.netAnnualBenefit,
            roi: allScenarios.realistic.roi,
            payback_months: allScenarios.realistic.paybackMonths,
            five_year_value: allScenarios.realistic.fiveYearValue,
          },
          optimistic: {
            efficiency_pct: Math.round(sf.optimistic * 100),
            hours_saved: allScenarios.optimistic.hoursSaved,
            cost_saved: allScenarios.optimistic.costSaved,
            net_annual_benefit: allScenarios.optimistic.netAnnualBenefit,
            roi: allScenarios.optimistic.roi,
            payback_months: allScenarios.optimistic.paybackMonths,
            five_year_value: allScenarios.optimistic.fiveYearValue,
          },
        },
        chart_data: [0, 12, 24, 36, 48, 60].map(m => {
          const y = m / 12;
          return {
            month: m,
            year: y,
            conservative: allScenarios.conservative.costSaved * y - projectCost - annualOpsCost * y,
            realistic: allScenarios.realistic.costSaved * y - projectCost - annualOpsCost * y,
            optimistic: allScenarios.optimistic.costSaved * y - projectCost - annualOpsCost * y,
          };
        }),
        break_even_months: breakEvenMonths,
      });
      setDossierUrl(res.data.full_download_url);
      toast.success("ROI-Dossier erstellt!");
    } catch {
      toast.error("Dossier konnte nicht erstellt werden. Ist Atlas erreichbar?");
    } finally {
      setGeneratingDossier(false);
    }
  };

  const catInfo = CATEGORIES.find(c => c.id === category);
  const scenarioFactor = SCENARIO_FACTORS[category];

  return (
    <div className="space-y-8" data-testid="roi-calculator">
      {/* Top Actions */}
      <div className="flex gap-3 flex-wrap items-center">
        <button onClick={openOfferPicker}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-white border border-[#CBD5E1] text-[#0F172A] hover:border-[#D4A373] hover:text-[#D4A373] transition-colors"
          data-testid="roi-load-kalkulator">
          <Upload className="w-4 h-4" /> Aus Kalkulator laden
        </button>
        <button onClick={generateDossier} disabled={generatingDossier}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors disabled:opacity-50"
          data-testid="roi-generate-dossier">
          {generatingDossier ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          {generatingDossier ? "Wird erstellt..." : "ROI-Dossier erstellen"}
        </button>
        {dossierUrl && (
          <a href={dossierUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-[#10B981] text-white hover:bg-[#059669] transition-colors"
            data-testid="roi-dossier-download">
            <ExternalLink className="w-4 h-4" /> PDF herunterladen
          </a>
        )}
        {loadedOffer && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[#D4A373]/10 border border-[#D4A373]/30 text-xs text-[#92400E]" data-testid="roi-loaded-offer-badge">
            Geladen: {loadedOffer.offer_number} — {loadedOffer.client_name}
            <button onClick={() => setLoadedOffer(null)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
          </div>
        )}
      </div>

      {/* Category Selection */}
      <div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          {CATEGORIES.slice(0, 3).map(cat => {
            const CatIcon = cat.icon;
            const sf = SCENARIO_FACTORS[cat.id];
            return (
              <button key={cat.id} onClick={() => setCategory(cat.id)}
                className={`p-4 border text-left transition-all ${category === cat.id ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#CBD5E1] bg-white text-[#0F172A] hover:border-[#D4A373]"}`}
                data-testid={`roi-cat-${cat.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <CatIcon className="w-4 h-4" style={{ color: cat.color }} />
                  <span className="text-sm font-semibold">{cat.label}</span>
                </div>
                <div className="text-[0.65rem] text-[#94A3B8]">{cat.desc}</div>
                <div className="mt-2 text-[0.6rem] font-bold" style={{ color: cat.color }}>
                  {Math.round(sf.conservative * 100)}–{Math.round(sf.optimistic * 100)}% Effizienz
                </div>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {CATEGORIES.slice(3).map(cat => {
            const CatIcon = cat.icon;
            const sf = SCENARIO_FACTORS[cat.id];
            return (
              <button key={cat.id} onClick={() => setCategory(cat.id)}
                className={`p-4 border text-left transition-all ${category === cat.id ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#CBD5E1] bg-white text-[#0F172A] hover:border-[#D4A373]"}`}
                data-testid={`roi-cat-${cat.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <CatIcon className="w-4 h-4" style={{ color: cat.color }} />
                  <span className="text-sm font-semibold">{cat.label}</span>
                </div>
                <div className="text-[0.65rem] text-[#94A3B8]">{cat.desc}</div>
                <div className="mt-2 text-[0.6rem] font-bold" style={{ color: cat.color }}>
                  {Math.round(sf.conservative * 100)}–{Math.round(sf.optimistic * 100)}% Effizienz
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Scenario Toggle */}
      <div className="flex items-center gap-2" data-testid="roi-scenario-toggle">
        <Sliders className="w-4 h-4 text-[#64748B]" />
        <span className="text-xs font-bold uppercase text-[#94A3B8] mr-2" style={{ letterSpacing: "0.1em" }}>Szenario</span>
        {Object.entries(SCENARIO_LABELS).map(([key, { label, color }]) => (
          <button key={key} onClick={() => setScenario(key)}
            className={`px-3 py-1.5 text-xs font-semibold border transition-all ${
              scenario === key
                ? "text-white border-transparent"
                : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#CBD5E1]"
            }`}
            style={scenario === key ? { backgroundColor: color, borderColor: color } : {}}
            data-testid={`roi-scenario-${key}`}>
            {label} ({Math.round(scenarioFactor[key] * 100)}%)
          </button>
        ))}
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Inputs */}
          <div className="bg-white border border-[#CBD5E1] p-6 space-y-6">
            <h3 className="text-xs font-bold uppercase text-[#94A3B8]" style={{ letterSpacing: "0.15em" }}>Parameter</h3>
            <SliderInput label="Betroffene Mitarbeitende" value={employees} onChange={setEmployees}
              min={1} max={50} step={1} unit="MA" testId="roi-employees" />
            <SliderInput label="Stunden/Woche für manuelle Aufgaben" value={hoursWeek} onChange={setHoursWeek}
              min={1} max={40} step={1} unit="h" testId="roi-hours" />
            <SliderInput label="Durchschnittliche Stundenkosten" value={hourlyCost} onChange={setHourlyCost}
              min={40} max={200} step={5} unit="CHF" testId="roi-cost" />
            <SliderInput label="Projektkosten (Einmalinvestition)" value={projectCost} onChange={setProjectCost}
              min={5000} max={200000} step={1000} unit="CHF" testId="roi-investment" />
            <SliderInput label="Laufende Kosten / Jahr (Wartung, Lizenzen, Support)" value={annualOpsCost} onChange={setAnnualOpsCost}
              min={0} max={50000} step={500} unit="CHF" testId="roi-ops-cost" />
          </div>

          {/* Right: Results */}
          <div className="space-y-5">
            <div className="bg-[#0F172A] p-6 border border-[#1E293B]">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-2 h-2 rounded-full" style={{ background: catInfo.color }} />
                <h3 className="text-xs font-bold uppercase text-[#94A3B8]" style={{ letterSpacing: "0.15em" }}>
                  {catInfo.label} — {SCENARIO_LABELS[scenario].label}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <ResultCard icon={Clock} label="Zeitersparnis / Jahr" value={`${active.hoursSaved.toLocaleString("de-CH")} h`}
                  sub={`von ${active.hoursPerYear.toLocaleString("de-CH")} h total`} color="#D4A373" />
                <ResultCard icon={DollarSign} label="Netto-Ersparnis / Jahr" value={formatCHF(active.netAnnualBenefit)}
                  sub={`${formatCHF(active.costSaved)} brutto − ${formatCHF(annualOpsCost)} lfd. Kosten`} color="#10B981" />
                <ResultCard icon={TrendingUp} label="ROI" value={`${active.roi > 0 ? "+" : ""}${active.roi}%`}
                  sub="im ersten Jahr" color={active.roi > 0 ? "#10B981" : "#EF4444"} />
                <ResultCard icon={Calculator} label="Amortisation" value={`${active.paybackMonths} Monate`}
                  sub={active.paybackMonths <= 12 ? "Unter 1 Jahr" : `${Math.round(active.paybackMonths / 12 * 10) / 10} Jahre`} color="#3B82F6" />
              </div>

              <div className="border-t border-[#334155] pt-4">
                <div className="text-center">
                  <div className="text-[0.6rem] font-bold uppercase text-[#64748B]" style={{ letterSpacing: "0.15em" }}>5-Jahres-Wert (abzgl. Investition + lfd. Kosten)</div>
                  <div className={`font-playfair text-3xl font-semibold mt-1 ${active.fiveYearValue > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {formatCHF(active.fiveYearValue)}
                  </div>
                </div>
              </div>
            </div>

            {/* Scenario Comparison */}
            <div className="bg-white border border-[#CBD5E1] p-5">
              <h3 className="text-xs font-bold uppercase text-[#94A3B8] mb-4" style={{ letterSpacing: "0.15em" }}>Szenario-Vergleich</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(SCENARIO_LABELS).map(([key, { label, color }]) => {
                  const sc = allScenarios[key];
                  return (
                    <div key={key}
                      className={`p-3 border text-center transition-all cursor-pointer ${scenario === key ? "border-2" : "border-[#E2E8F0] hover:border-[#CBD5E1]"}`}
                      style={scenario === key ? { borderColor: color } : {}}
                      onClick={() => setScenario(key)}
                      data-testid={`roi-compare-${key}`}>
                      <div className="text-[0.6rem] font-bold uppercase mb-1" style={{ color, letterSpacing: "0.1em" }}>{label}</div>
                      <div className="font-playfair text-lg font-semibold text-[#0F172A]">{sc.roi > 0 ? "+" : ""}{sc.roi}%</div>
                      <div className="text-[0.6rem] text-[#94A3B8]">ROI 1. Jahr</div>
                      <div className="text-xs font-semibold text-[#0F172A] mt-1">{formatCHF(sc.fiveYearValue)}</div>
                      <div className="text-[0.6rem] text-[#94A3B8]">5-Jahres-Wert</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Before/After */}
            <div className="bg-white border border-[#CBD5E1] p-5">
              <h3 className="text-xs font-bold uppercase text-[#94A3B8] mb-4" style={{ letterSpacing: "0.15em" }}>Vorher / Nachher</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs text-[#64748B] mb-1">
                    <span>Manueller Aufwand vorher</span>
                    <span>{active.hoursPerYear.toLocaleString("de-CH")} h/Jahr</span>
                  </div>
                  <div className="h-6 bg-[#EF4444]/15 w-full relative">
                    <div className="h-full bg-[#EF4444]/60" style={{ width: "100%" }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs text-[#64748B] mb-1">
                    <span>Aufwand nachher</span>
                    <span>{(active.hoursPerYear - active.hoursSaved).toLocaleString("de-CH")} h/Jahr</span>
                  </div>
                  <div className="h-6 bg-[#10B981]/15 w-full relative">
                    <motion.div
                      className="h-full bg-[#10B981]/60"
                      initial={{ width: "100%" }}
                      animate={{ width: `${(1 - active.timeSaving) * 100}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5-Year Chart with all 3 scenarios */}
        <div className="bg-white border border-[#CBD5E1] p-6" data-testid="roi-5year-chart">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-xs font-bold uppercase text-[#94A3B8]" style={{ letterSpacing: "0.15em" }}>5-Jahres-Projektion (alle Szenarien)</h3>
            {breakEvenMonths && (
              <div className="text-xs font-semibold" style={{ color: SCENARIO_LABELS[scenario].color }}>
                Break-Even nach {breakEvenMonths} Monaten ({SCENARIO_LABELS[scenario].label})
              </div>
            )}
          </div>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="conservGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#94A3B8" stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="realGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="optGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="month"
                  type="number"
                  domain={[0, 60]}
                  ticks={[0, 12, 24, 36, 48, 60]}
                  tick={{ fontSize: 11, fill: "#94A3B8" }}
                  tickFormatter={v => `Jahr ${v / 12}`}
                  axisLine={{ stroke: "#CBD5E1" }}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} tickFormatter={v => `${Math.round(v / 1000)}k`} axisLine={false} tickLine={false} width={50} />
                <Tooltip content={<ChartTooltip />} />
                <ReferenceLine y={0} stroke="#CBD5E1" strokeWidth={1} />
                <Area type="monotone" dataKey="optimistic" name="Optimistisch" stroke="#3B82F6" strokeWidth={scenario === "optimistic" ? 2.5 : 1} fill="url(#optGrad)" strokeDasharray={scenario === "optimistic" ? "0" : "4 3"} />
                <Area type="monotone" dataKey="realistic" name="Realistisch" stroke="#10B981" strokeWidth={scenario === "realistic" ? 2.5 : 1} fill="url(#realGrad)" strokeDasharray={scenario === "realistic" ? "0" : "4 3"} />
                <Area type="monotone" dataKey="conservative" name="Konservativ" stroke="#94A3B8" strokeWidth={scenario === "conservative" ? 2.5 : 1} fill="url(#conservGrad)" strokeDasharray={scenario === "conservative" ? "0" : "4 3"} />
                {breakEvenMonths && breakEvenMonths <= 60 && (
                  <ReferenceLine x={breakEvenMonths} stroke="#D4A373" strokeWidth={1.5} strokeDasharray="4 4"
                    label={{ value: `Break-Even (${breakEvenMonths} Mt.)`, position: "top", fontSize: 10, fill: "#D4A373", fontWeight: 600 }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-3">
            {Object.entries(SCENARIO_LABELS).map(([key, { label, color }]) => (
              <button key={key} onClick={() => setScenario(key)}
                className={`flex items-center gap-2 text-xs transition-colors ${scenario === key ? "font-semibold" : "text-[#94A3B8] hover:text-[#64748B]"}`}
                style={scenario === key ? { color } : {}}>
                <span className="w-3 h-0.5 inline-block" style={{ backgroundColor: color, opacity: scenario === key ? 1 : 0.5 }} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Szenario speichern */}
      <div className="bg-white border border-[#CBD5E1] p-5">
        <h3 className="text-xs font-bold uppercase text-[#94A3B8] mb-4" style={{ letterSpacing: "0.15em" }}>Szenario speichern</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input value={clientName} onChange={e => setClientName(e.target.value)}
            className="border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#D4A373] outline-none"
            placeholder="Kundenname (optional)" data-testid="roi-client" />
          <input value={notes} onChange={e => setNotes(e.target.value)}
            className="border border-[#CBD5E1] px-3 py-2 text-sm focus:border-[#D4A373] outline-none"
            placeholder="Notizen..." data-testid="roi-notes" />
        </div>
        <button onClick={saveScenario}
          className="mt-3 bg-[#0F172A] text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#1E293B] transition-colors"
          data-testid="roi-save">
          <Save className="w-4 h-4" /> Speichern
        </button>
      </div>

      {/* Saved Scenarios */}
      {scenarios.length > 0 && (
        <div className="bg-white border border-[#CBD5E1]">
          <button onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-[#0F172A] hover:bg-[#F8F9FA] transition-colors"
            data-testid="roi-history-toggle">
            <span>Gespeicherte Szenarien ({scenarios.length})</span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showHistory && (
            <div className="border-t border-[#CBD5E1] divide-y divide-[#F1F5F9]">
              {scenarios.map(s => {
                const cat = CATEGORIES.find(c => c.id === s.category);
                return (
                  <div key={s.id} className="px-5 py-3 flex items-center gap-4" data-testid={`roi-scenario-${s.id}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cat?.color || "#94A3B8" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">
                        {s.client_name || "Ohne Kunde"} — {cat?.label || s.category}
                      </div>
                      <div className="text-xs text-[#64748B]">
                        ROI: {s.results?.roi || 0}% | {formatCHF(s.results?.netAnnualBenefit || s.results?.costSaved || 0)}/Jahr | {new Date(s.created_at).toLocaleDateString("de-CH")}
                      </div>
                      {s.loaded_offer && (
                        <div className="text-[0.65rem] text-[#D4A373] mt-0.5">Angebot: {s.loaded_offer.offer_number}</div>
                      )}
                      {s.notes && <div className="text-xs text-[#94A3B8] mt-0.5 truncate">{s.notes}</div>}
                    </div>
                    <button onClick={() => loadScenarioData(s)}
                      className="text-xs text-[#D4A373] hover:underline px-2" data-testid={`roi-load-${s.id}`}>
                      Laden
                    </button>
                    <button onClick={() => deleteScenario(s.id)}
                      className="text-xs text-red-400 hover:text-red-600 px-1" data-testid={`roi-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Offer Picker Modal */}
      {showOfferPicker && (
        <OfferPickerModal
          offers={kalkulatorOffers}
          onSelect={handleOfferSelect}
          onClose={() => setShowOfferPicker(false)}
        />
      )}
    </div>
  );
}
